<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import { useErrorHandler } from '@/composables/useErrorHandler'
import { useTour } from '@/composables/useTour'
import { managelistTourSteps } from '@/composables/tours/managelistTour'
import { getRoleDisplayName } from '@/utils/roleUtils'

// Lazy load heavy ConditionBuilder for better performance
const ConditionBuilder = defineAsyncComponent(() => import('@/components/lead-assignment/ConditionBuilder.vue'))

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'create',
    subject: 'LeadAssignment',
  },
})

const { t } = useI18n()
const { showToast } = useToast()
const { handleError } = useErrorHandler()
const { startTour } = useTour()

// 👉 Assignment Mode
type AssignmentMode = 'pool-to-user' | 'user-to-user'

const assignmentMode = ref<AssignmentMode>('pool-to-user')

// Assignment modes for CustomRadios
const assignmentModes = computed(() => [
  {
    title: t('leadAssignment.poolToUser'),
    desc: t('leadAssignment.poolToUserDesc'),
    value: 'pool-to-user',
    icon: { icon: 'tabler-database-import', size: '28' },
  },
  {
    title: t('leadAssignment.userToUser'),
    desc: t('leadAssignment.userToUserDesc'),
    value: 'user-to-user',
    icon: { icon: 'tabler-user-share', size: '28' },
  },
])

// 👉 Step 1: Assignment Configuration
const selectedCampaign = ref<number | null>(null)
const selectedCallLists = ref<number[]>([])
const selectedRole = ref<number | null>(null)

// 👉 Step 2: Condition Builder
const conditions = ref<any[]>([])

// 👉 Step 3: Target Users & Settings
const selectedTargetUsers = ref<number[]>([])
const selectedWrapupStatus = ref<number | null>(null)
const leadsPerUser = ref<number | null>(null) // จำนวนลีสที่ต้องการแจกต่อคน

// 👉 User-to-User Mode: Source User Selection
const selectedSourceUsers = ref<number[]>([]) // Multiple source users
const sourceWrapupStatus = ref<number | null>(null) // Filter by wrapup status for source

// 👉 Data Loading
const loading = ref(false)
const assigning = ref(false)
const campaigns = ref<any[]>([])
const callLists = ref<any[]>([])
const roles = ref<any[]>([])
const users = ref<any[]>([])
const sourceUsers = ref<any[]>([]) // For user-to-user mode
const wrapupStatuses = ref<any[]>([])
const newSystemStatus = ref<any>(null) // NEW system status for default
const customFields = ref<any[]>([])
// Removed leadPreview (not used in UI)
const matchingLeadsCount = ref<{ count: number; dncCount: number; totalCount: number } | null>(null) // Count of leads matching conditions + configuration
const matchingLeadsLoading = ref(false)

// 👉 UI State
const expandedPanels = ref([0]) // Default expand panels: Configuration (0)

// 👉 Target Users DataTable Headers
const targetUsersHeaders = computed(() => {
  const baseHeaders = [
    { title: t('users.user'), key: 'user', sortable: true },
  ]

  // Add wrapup status columns
  const statusHeaders = wrapupStatuses.value.map(status => ({
    title: status.name,
    key: `status_${status.id}`,
    sortable: true,
    align: 'center' as const,
  }))

  // Add total column
  const totalHeader = {
    title: t('common.total'),
    key: 'total',
    sortable: true,
    align: 'center' as const,
  }

  return [...baseHeaders, ...statusHeaders, totalHeader]
})

// 👉 Source Users DataTable Headers (for user-to-user)
const sourceUsersHeaders = computed(() => targetUsersHeaders.value)

// 👉 Computed: Total assignable leads for user-to-user mode
const totalAssignableLeads = computed(() => {
  if (assignmentMode.value !== 'user-to-user' || !sourceWrapupStatus.value)
    return 0

  return selectedSourceUsers.value.reduce((total, userId) => {
    const user = sourceUsers.value.find((u: any) => u.id === userId)

    return total + (user?.leadCounts?.[sourceWrapupStatus.value!] || 0)
  }, 0)
})

// 👉 Computed: Total assignable DNC leads for user-to-user mode
const totalAssignableLeadsDnc = computed(() => {
  if (assignmentMode.value !== 'user-to-user' || !sourceWrapupStatus.value)
    return 0

  return selectedSourceUsers.value.reduce((total, userId) => {
    const user = sourceUsers.value.find((u: any) => u.id === userId)

    return total + (user?.dncLeadCounts?.[sourceWrapupStatus.value!] || 0)
  }, 0)
})

// 👉 Computed: Total new leads for pool-to-user mode
const totalNewLeads = computed(() => {
  if (assignmentMode.value !== 'pool-to-user' || selectedCallLists.value.length === 0)
    return 0

  return selectedCallLists.value.reduce((total, callListId) => {
    const callList = callLists.value.find((cl: any) => cl.id === callListId)

    // Assuming call list has a field for new leads count
    return total + (callList?.newLeadsCount || 0)
  }, 0)
})

// 👉 Computed: Total new DNC leads for pool-to-user mode
const totalNewLeadsDnc = computed(() => {
  if (assignmentMode.value !== 'pool-to-user' || selectedCallLists.value.length === 0)
    return 0

  return selectedCallLists.value.reduce((total, callListId) => {
    const callList = callLists.value.find((cl: any) => cl.id === callListId)

    // ✅ Use newDncLeadsCount from Lead table (not dncCount from staging)
    return total + (callList?.newDncLeadsCount || 0)
  }, 0)
})

// 👉 Computed: Total selected leads from configuration only (no conditions)
const totalSelectedLeads = computed(() => {
  if (assignmentMode.value === 'pool-to-user')
    return totalNewLeads.value
  else
    return totalAssignableLeads.value
})

// 👉 Computed: Final leads count for assignment (with conditions if available)
const finalLeadsCount = computed(() => {
  // If conditions are set and we have matching count, use it
  if (conditions.value.length > 0 && matchingLeadsCount.value !== null)
    return matchingLeadsCount.value.count

  // Otherwise use configuration-based count
  return totalSelectedLeads.value
})

// 👉 Computed: Total DNC leads from configuration only (no conditions)
const totalSelectedDncLeads = computed(() => {
  if (assignmentMode.value === 'pool-to-user')
    return totalNewLeadsDnc.value
  else
    return totalAssignableLeadsDnc.value
})

// 👉 Computed: DNC leads count (configuration only, NOT affected by conditions)
const dncLeadsCount = computed(() => {
  // Always use configuration-based DNC count (not affected by condition builder)
  return totalSelectedDncLeads.value
})

// 👉 Computed: Maximum leads per user (based on final count)
const maxLeadsPerUser = computed(() => {
  if (selectedTargetUsers.value.length === 0)
    return 0

  return Math.floor(finalLeadsCount.value / selectedTargetUsers.value.length)
})

// 👉 Computed: Check if there are duplicate users between source and target
const duplicateUsers = computed(() => {
  if (assignmentMode.value !== 'user-to-user')
    return []

  return selectedTargetUsers.value.filter(userId => 
    selectedSourceUsers.value.includes(userId)
  )
})

const hasDuplicateUsers = computed(() => duplicateUsers.value.length > 0)

// 👉 Helper Functions
const getStatusColor = (groupStatus: string) => {
  const colorMap: Record<string, string> = {
    NEW: 'info',
    CONTACTED: 'warning',
    QUALIFIED: 'success',
    UNQUALIFIED: 'error',
    CONVERTED: 'success',
    CANCELLED: 'secondary',
  }

  return colorMap[groupStatus] || 'default'
}

const calculateTotalLeads = (user: any) => {
  if (!user.leadCounts)
    return 0

  return Object.values(user.leadCounts as Record<number, number>).reduce((sum: number, count: number) => sum + count, 0)
}

// 👉 Load Campaigns (with permission filtering)
const loadCampaigns = async () => {
  try {
    loading.value = true

    const { data } = await useApi(createUrl('/campaigns/active'))

    campaigns.value = data.value || []
  }
  catch (error) {
    handleError(error, t('campaign.error'))
  }
  finally {
    loading.value = false
  }
}

// 👉 Load Call Lists (filtered by campaign)
const loadCallLists = async () => {
  if (!selectedCampaign.value) {
    callLists.value = []

    return
  }

  try {
    // Use campaign-specific endpoint to get only call lists mapped to this campaign
    const { data } = await useApi(createUrl(`/campaigns/${selectedCampaign.value}/call-lists/active`))

    callLists.value = data.value || []
  }
  catch (error) {
    handleError(error, t('calllist.error'))
  }
}

// 👉 Load Roles
const loadRoles = async () => {
  try {
    const { data } = await useApi(createUrl('/roles/active'))

    roles.value = data.value || []
  }
  catch (error) {
    handleError(error, t('roles.error'))
  }
}

// 👉 Load Users (filtered by campaign & role)
const loadUsers = async (isSource = false) => {
  if (!selectedCampaign.value) {
    if (isSource)
      sourceUsers.value = []
    else
      users.value = []

    return
  }

  try {
    // Use campaign-specific endpoint to get only users mapped to this campaign
    const { data } = await useApi(createUrl(`/campaigns/${selectedCampaign.value}/users`))

    let filteredUsers = data.value || []

    // Apply role filter if selected
    if (selectedRole.value)
      filteredUsers = filteredUsers.filter((user: any) => user.role?.id === selectedRole.value)

    // Fetch lead counts (both normal and DNC)
    const [leadCountsResponse, dncLeadCountsResponse] = await Promise.all([
      useApi(createUrl('/lead-assignments/user-lead-counts', {
        query: {
          campaignId: selectedCampaign.value,
          userIds: filteredUsers.map((u: any) => u.id).join(','),
        },
      })),
      useApi(createUrl('/lead-assignments/user-dnc-lead-counts', {
        query: {
          campaignId: selectedCampaign.value,
          userIds: filteredUsers.map((u: any) => u.id).join(','),
        },
      })),
    ])

    const leadCountsMap = leadCountsResponse.data.value || {}
    const dncLeadCountsMap = dncLeadCountsResponse.data.value || {}

    // Map users with lead counts (create new array for reactivity)
    const usersWithCounts = filteredUsers.map((user: any) => ({
    ...user,
      leadCounts: leadCountsMap[user.id] || {},
      dncLeadCounts: dncLeadCountsMap[user.id] || {}
    }))

    if (isSource)
      sourceUsers.value = usersWithCounts
    else
      users.value = usersWithCounts
  }
  catch (error) {
    handleError(error, t('users.error'))
  }
}

// 👉 Load Wrapup Statuses (only those shown in Call Work)
const loadWrapupStatuses = async () => {
  try {
    // Use call-work endpoint to get only statuses with showInCallWork = true
    const { data } = await useApi(createUrl('/wrapup-group-status/call-work'))

    wrapupStatuses.value = data.value || []
  }
  catch (error) {
    handleError(error, t('wrapup.error'))
  }
}

// 👉 Load NEW System Status
const loadNewSystemStatus = async () => {
  try {
    const { data } = await useApi(createUrl('/wrapup-group-status/system/new'))

    newSystemStatus.value = data.value

    // Set NEW as default for pool-to-user mode
    if (assignmentMode.value === 'pool-to-user' && !selectedWrapupStatus.value)
      selectedWrapupStatus.value = newSystemStatus.value?.id
  }
  catch (error) {
    console.error('Failed to load NEW system status:', error)
  }
}

// 👉 Load Matching Leads Count (with conditions)
const loadMatchingLeadsCount = async () => {
  // Only count if we have necessary configuration
  if (!selectedCampaign.value)
    return

  // For pool-to-user: need call lists or campaign
  // For user-to-user: need source users and status
  if (assignmentMode.value === 'pool-to-user') {
    // If no conditions and no call lists selected, use totalNewLeads
    if (conditions.value.length === 0 || selectedCallLists.value.length === 0) {
      matchingLeadsCount.value = null
      return
    }
  }
  else {
    // user-to-user
    if (conditions.value.length === 0 || selectedSourceUsers.value.length === 0 || !sourceWrapupStatus.value) {
      matchingLeadsCount.value = null
      return
    }
  }

  try {
    matchingLeadsLoading.value = true

    // Determine target wrapup status based on mode
    let targetWrapupStatusId: number | null = null

    if (assignmentMode.value === 'pool-to-user') {
      targetWrapupStatusId = newSystemStatus.value?.id || selectedWrapupStatus.value
    }
    else {
      targetWrapupStatusId = sourceWrapupStatus.value
    }

    if (!targetWrapupStatusId)
      return

    // Filter out incomplete conditions (missing field, operator, or value)
    const validConditions = conditions.value.filter(
      (c: any) => c.field && c.operator && (c.value !== '' && c.value !== null && c.value !== undefined),
    )

    // If user added conditions but didn't complete them, don't count
    if (conditions.value.length > 0 && validConditions.length === 0) {
      matchingLeadsCount.value = null
      return
    }

    const requestData: any = {
      mode: assignmentMode.value,
      campaignId: selectedCampaign.value,
      userIds: [1], // Dummy value (required but not used for counting)
      wrapupGroupStatusId: targetWrapupStatusId,
      conditions: validConditions,
    }

    if (selectedCallLists.value.length > 0)
      requestData.callListIds = selectedCallLists.value

    if (assignmentMode.value === 'user-to-user') {
      if (selectedSourceUsers.value.length > 0)
        requestData.sourceUserIds = selectedSourceUsers.value
      if (sourceWrapupStatus.value)
        requestData.sourceWrapupStatusId = sourceWrapupStatus.value
    }

    const response = await $api('/lead-assignments/count-matching-leads', {
      method: 'POST',
      body: requestData,
    })

    matchingLeadsCount.value = {
      count: response.count || 0,
      dncCount: response.dncCount || 0,
      totalCount: response.totalCount || 0,
    }
  }
  catch (error) {
    console.error('Failed to count matching leads:', error)
    matchingLeadsCount.value = null
  }
  finally {
    matchingLeadsLoading.value = false
  }
}

// 👉 Load Custom Fields
const loadCustomFields = async () => {
  try {
    const { data } = await useApi(createUrl('/custom-fields/active'))

    customFields.value = data.value || []
  }
  catch (error) {
    console.error('Failed to load custom fields:', error)

    // Not critical, just log the error
  }
}

// 👉 Assign Leads
const assignLeads = async () => {
  if (!selectedCampaign.value || selectedTargetUsers.value.length === 0) {
    showToast({
      type: 'error',
      message: t('leadAssignment.validationError'),
    })

    return
  }

  try {
    assigning.value = true

    // Determine target wrapup status based on mode
    let targetWrapupStatusId: number | null = null

    if (assignmentMode.value === 'pool-to-user') {
      // Pool-to-user: Use NEW system status as default
      targetWrapupStatusId = newSystemStatus.value?.id || selectedWrapupStatus.value
    }
    else {
      // User-to-user: Use source wrapup status (from filter)
      targetWrapupStatusId = sourceWrapupStatus.value
    }

    if (!targetWrapupStatusId) {
      showToast({
        type: 'error',
        message: t('leadAssignment.validationError'),
      })

      return
    }

    // Filter out incomplete conditions (missing field, operator, or value)
    const validConditions = conditions.value.filter(
      (c: any) => c.field && c.operator && (c.value !== '' && c.value !== null && c.value !== undefined),
    )

    const assignmentData: any = {
      mode: assignmentMode.value,
      campaignId: selectedCampaign.value,
      userIds: selectedTargetUsers.value,
      wrapupGroupStatusId: targetWrapupStatusId,
      conditions: validConditions.length > 0 ? validConditions : undefined,
      leadsPerUser: leadsPerUser.value || undefined,
    }

    if (selectedCallLists.value.length > 0)
      assignmentData.callListIds = selectedCallLists.value

    if (assignmentMode.value === 'user-to-user') {
      if (selectedSourceUsers.value.length > 0)
        assignmentData.sourceUserIds = selectedSourceUsers.value
      if (sourceWrapupStatus.value)
        assignmentData.sourceWrapupStatusId = sourceWrapupStatus.value
    }

    const response = await $api('/lead-assignments/assign', {
      method: 'POST',
      body: assignmentData,
    })

    showToast({
      type: 'success',
      message: t('leadAssignment.assignedSuccessfully', { count: response.assigned }),
    })

    // Reset form
    resetForm()
  }
  catch (error) {
    handleError(error, t('leadAssignment.assignError'))
  }
  finally {
    assigning.value = false
  }
}

// 👉 Reset Form
const resetForm = () => {
  selectedCampaign.value = null
  selectedCallLists.value = []
  selectedRole.value = null
  selectedSourceUsers.value = []
  sourceWrapupStatus.value = null
  conditions.value = []
  selectedTargetUsers.value = []
  selectedWrapupStatus.value = null
  leadsPerUser.value = null
}

// 👉 Validation
const canAssign = computed(() => {
  const hasValidConfig = selectedCampaign.value !== null
                        && (assignmentMode.value === 'pool-to-user' || (selectedSourceUsers.value.length > 0 && sourceWrapupStatus.value !== null))

  // For pool-to-user: need target users only (status will be NEW by default)
  // For user-to-user: need target users and source wrapup status
  const hasValidTargets = assignmentMode.value === 'pool-to-user'
    ? selectedTargetUsers.value.length > 0
    : selectedTargetUsers.value.length > 0 && sourceWrapupStatus.value !== null

  // Check for duplicate users in user-to-user mode
  const noDuplicates = assignmentMode.value === 'pool-to-user' || !hasDuplicateUsers.value

  return hasValidConfig && hasValidTargets && noDuplicates
})

// 👉 Handle Assign
const handleAssign = async () => {
  await assignLeads()
}

// 👉 Watchers
watchDebounced(() => selectedCampaign.value, () => {
  // Reset dependent selections when campaign changes
  selectedCallLists.value = []
  selectedSourceUsers.value = []
  sourceWrapupStatus.value = null
  selectedTargetUsers.value = []

  // Load campaign-specific data
  loadCallLists()
  loadUsers()
  if (assignmentMode.value === 'user-to-user')
    loadUsers(true)
}, { debounce: 200 })

watchDebounced(() => selectedRole.value, () => {
  // Reload users when role filter changes
  loadUsers()
  if (assignmentMode.value === 'user-to-user')
    loadUsers(true)
}, { debounce: 200 })

watch(() => assignmentMode.value, () => {
  // Reset source users when mode changes
  selectedSourceUsers.value = []
  sourceWrapupStatus.value = null

  // Set default status based on mode
  if (assignmentMode.value === 'pool-to-user') {
    // Set NEW as default for pool-to-user
    selectedWrapupStatus.value = newSystemStatus.value?.id || null
  }
  else {
    // Clear for user-to-user (will use sourceWrapupStatus instead)
    selectedWrapupStatus.value = null
  }

  // Load source users for user-to-user mode
  if (assignmentMode.value === 'user-to-user' && selectedCampaign.value)
    loadUsers(true)
})

// 👉 Watch conditions and configuration to update matching leads count (debounced for performance)
watchDebounced([
  () => conditions.value,
  () => selectedCallLists.value,
  () => selectedSourceUsers.value,
  () => sourceWrapupStatus.value,
], () => {
  loadMatchingLeadsCount()
}, { 
  debounce: 300,
  deep: true,
})

// 👉 Mount
onMounted(async () => {
  loadCampaigns()
  loadRoles()
  loadWrapupStatuses()
  loadCustomFields()
  await loadNewSystemStatus()

  // Start managelist tour
  startTour({
    tourName: 'managelist',
    steps: managelistTourSteps,
  })
})
</script>

<template>
  <div>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('leadAssignment.title') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('leadAssignment.description') }}
      </p>
    </div>

    <VCard>
      <VCardText>
        <!-- 👉 Assignment Mode Selector -->
        <h6 class="text-h6 font-weight-medium mb-4">
          {{ t('leadAssignment.assignmentMode') }}
        </h6>
        <div data-tour="assignment-mode">
          <CustomRadiosWithIcon
            v-model:selected-radio="assignmentMode"
            :radio-content="assignmentModes"
            :grid-column="{ cols: '12', sm: '6' }"
          />
        </div>
      </VCardText>

      <VDivider />

      <VCardText>
        <!-- 👉 Configuration & Conditions -->
        <VExpansionPanels
          v-model="expandedPanels"
          multiple
          variant="accordion"
        >
          <!-- Panel 1: Configuration -->
          <VExpansionPanel
            elevation="0"
            class="border rounded mb-4"
            data-tour="configuration-panel"
          >
            <VExpansionPanelTitle class="text-h6">
              <div class="d-flex align-center gap-3">
                <VIcon
                  icon="tabler-settings"
                  size="24"
                />
                <div>
                  <div class="font-weight-medium">
                    {{ t('leadAssignment.configuration') }}
                  </div>
                  <div class="text-caption text-medium-emphasis font-weight-regular">
                    {{ t('leadAssignment.configurationDescription') }}
                  </div>
                </div>
              </div>
            </VExpansionPanelTitle>
            <VExpansionPanelText>
          <VRow>
          <!-- Campaign Selection -->
            <VCol
              cols="12"
              md="6"
            >
                  <AppSelect
                    v-model="selectedCampaign"
                    :label="t('campaign.campaign')"
                    :placeholder="t('campaign.selectCampaign')"
                :items="campaigns"
                item-title="name"
                item-value="id"
                    :loading="loading"
                    :rules="[requiredValidator]"
                  >
                    <template #prepend-inner>
                      <VIcon icon="tabler-speakerphone" />
                    </template>
                  </AppSelect>
            </VCol>

                <!-- Call Lists (Optional) -->
            <VCol
              cols="12"
              md="6"
            >
                  <AppSelect
                    v-model="selectedCallLists"
                    :label="t('campaign.callLists')"
                    :placeholder="t('campaign.selectCallLists')"
                :items="callLists"
                    item-title="listName"
                item-value="id"
                    :disabled="!selectedCampaign"
                    multiple
                    chips
                    closable-chips
                  >
                    <template #prepend-inner>
                      <VIcon icon="tabler-list" />
                    </template>
                  </AppSelect>
            </VCol>

                <!-- Role Filter (Optional) -->
            <VCol
              cols="12"
              md="6"
            >
                  <AppSelect
                    v-model="selectedRole"
                    :label="t('roles.role')"
                    :placeholder="t('roles.selectRole')"
                :items="roles"
                item-title="name"
                item-value="id"
                clearable
                  >
                    <template #prepend-inner>
                      <VIcon icon="tabler-shield" />
                    </template>
                    <template #item="{ props: itemProps, item }">
                      <VListItem v-bind="itemProps">
                        <template #title>
                          {{ getRoleDisplayName(item.raw.name) }}
                        </template>
                      </VListItem>
                    </template>
                    <template #selection="{ item }">
                      {{ getRoleDisplayName(item.raw.name) }}
                    </template>
                  </AppSelect>
            </VCol>
          </VRow>
            </VExpansionPanelText>
          </VExpansionPanel>

          <!-- Panel 2: Conditions -->
          <VExpansionPanel
            elevation="0"
            class="border rounded"
            data-tour="condition-builder"
          >
            <VExpansionPanelTitle class="text-h6">
              <div class="d-flex align-center gap-3">
                <VIcon
                  icon="tabler-filter"
                  size="24"
                />
                <div>
                  <div class="font-weight-medium">
                    {{ t('leadAssignment.conditions') }}
                  </div>
                  <div class="text-caption text-medium-emphasis font-weight-regular">
                    {{ t('leadAssignment.conditionsDescription') }}
                  </div>
                </div>
              </div>
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <ConditionBuilder
                v-model="conditions"
                :mode="assignmentMode"
                :custom-fields="customFields"
              />
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCardText>

      <VDivider />

      <VCardText>
        <!-- 👉 Lead Distribution Summary -->
        <VRow class="mb-6">
          <VCol cols="12">
            <VCard
              variant="tonal"
              color="success"
            >
              <VCardText>
                <VRow align="center">
                  <!-- จำนวนลีสจาก Configuration -->
            <VCol
              cols="12"
                    :md="conditions.length > 0 ? 3 : 4"
                  >
                    <div class="d-flex flex-column">
                      <div class="text-caption text-medium-emphasis mb-1">
                        {{ t('leadAssignment.totalSelectedLeads') }}
                      </div>
                      <div class="d-flex align-center gap-2">
                        <VIcon
                          icon="tabler-list-check"
                          size="32"
                          class="text-success"
                        />
                        <div class="text-h4 font-weight-bold">
                          {{ totalSelectedLeads.toLocaleString() }}
                        </div>
                      </div>
                      <div class="text-caption text-medium-emphasis mt-1">
                        จาก Configuration
                      </div>
                      <div v-if="dncLeadsCount > 0" class="mt-2">
                        <VChip
                          color="error"
                          size="small"
                          variant="tonal"
                        >
                          <VIcon icon="tabler-ban" size="16" class="me-1" />
                          DNC: {{ dncLeadsCount.toLocaleString() }}
                        </VChip>
                      </div>
                    </div>
            </VCol>

                  <!-- จำนวนลีสที่ Match Conditions (แสดงเฉพาะเมื่อมี conditions) -->
                  <VCol
                    v-if="conditions.length > 0"
                    cols="12"
                    md="3"
                  >
                    <div class="d-flex flex-column">
                      <div class="text-caption text-medium-emphasis mb-1">
                        จำนวน Leads ที่ Match
                      </div>
                      <div class="d-flex align-center gap-2">
                        <VIcon
                          icon="tabler-filter-check"
                          size="32"
                          class="text-primary"
                        />
                        <div class="text-h4 font-weight-bold">
                          <VProgressCircular
                            v-if="matchingLeadsLoading"
                            indeterminate
                            size="24"
                            width="3"
                          />
                          <span v-else>{{ finalLeadsCount.toLocaleString() }}</span>
                        </div>
                      </div>
                      <div class="text-caption text-medium-emphasis mt-1">
                        Config + Conditions
                      </div>
                    </div>
            </VCol>

                  <!-- จำนวนลีสสูงสุดที่สามารถแจกได้ -->
            <VCol
              cols="12"
                    :md="conditions.length > 0 ? 3 : 4"
                  >
                    <div class="d-flex flex-column">
                      <div class="text-caption text-medium-emphasis mb-1">
                        {{ t('leadAssignment.maxLeadsPerUser') }}
                      </div>
                      <div class="d-flex align-center gap-2">
                        <VIcon
                          icon="tabler-calculator"
                          size="32"
                          class="text-info"
                        />
                        <div class="text-h4 font-weight-bold">
                          {{ maxLeadsPerUser.toLocaleString() }}
                        </div>
                      </div>
                      <div
                        v-if="selectedTargetUsers.length > 0"
                        class="text-caption text-medium-emphasis mt-1"
                      >
                        {{ finalLeadsCount.toLocaleString() }} ÷ {{ selectedTargetUsers.length }} {{ t('leadAssignment.users') }}
                      </div>
                    </div>
            </VCol>

                  <!-- จำนวนลีสที่ต้องการแจกต่อคน -->
            <VCol
              cols="12"
                    :md="conditions.length > 0 ? 3 : 4"
                  >
                    <AppTextField
                      v-model.number="leadsPerUser"
                      type="number"
                      :label="t('leadAssignment.leadsPerUser')"
                      :placeholder="t('leadAssignment.enterLeadsPerUser')"
                      :hint="maxLeadsPerUser > 0 ? t('leadAssignment.maxLeadsPerUserHint', { max: maxLeadsPerUser }) : ''"
                      persistent-hint
                      :disabled="maxLeadsPerUser === 0"
                      :rules="[
                        (v: number) => !v || v <= maxLeadsPerUser || t('leadAssignment.leadsPerUserExceedsMax', { max: maxLeadsPerUser }),
                        (v: number) => !v || v > 0 || t('leadAssignment.leadsPerUserMustBePositive'),
                      ]"
                    >
                      <template #prepend-inner>
                        <VIcon icon="tabler-user-check" />
                      </template>
                    </AppTextField>
                  </VCol>
                </VRow>
              </VCardText>
            </VCard>
            </VCol>
          </VRow>

        <!-- 👉 Review & Targets (2 Columns) -->
        <VRow class="match-height">
          <!-- Left Column: Source (Pool or User) -->
          <VCol
            cols="12"
            lg="6"
          >
            <VCard>
              <VCardTitle class="d-flex align-center gap-3">
                <VIcon
                  :icon="assignmentMode === 'pool-to-user' ? 'tabler-database-import' : 'tabler-user'"
                  size="24"
                />
                <div>
                  <div class="text-h6">
                    {{ assignmentMode === 'pool-to-user' ? t('leadAssignment.step1SourceCalllist') : t('leadAssignment.step1SourceUser') }}
          </div>
                  <div class="text-caption text-medium-emphasis font-weight-regular">
                    {{ assignmentMode === 'pool-to-user' ? t('leadAssignment.step1SourceCalllistDesc') : t('leadAssignment.step1SourceUserDesc') }}
                  </div>
                </div>
              </VCardTitle>
              <VCardText>
                <!-- Pool-to-User: Show New Leads Summary -->
                <template v-if="assignmentMode === 'pool-to-user'">
                  <VAlert
                    v-if="selectedCallLists.length === 0"
                    type="info"
                    variant="tonal"
                    class="mb-4"
                  >
                    {{ t('leadAssignment.selectCallListsFirst') }}
                  </VAlert>
                  <div v-else>
                    <!-- Selected Call Lists -->
                    <VList>
                      <VListSubheader>
                        {{ t('leadAssignment.selectedCallLists') }} ({{ selectedCallLists.length }})
                      </VListSubheader>
                      <VListItem
                        v-for="callListId in selectedCallLists"
                        :key="callListId"
                        :title="callLists.find((cl: any) => cl.id === callListId)?.listName"
                      >
                        <template #append>
                          <VChip
                            size="small"
                            color="success"
                            variant="tonal"
                          >
                            {{ (callLists.find((cl: any) => cl.id === callListId)?.newLeadsCount || 0).toLocaleString() }}
                            {{ t('leadAssignment.newLeads') }}
                          </VChip>
                        </template>
                      </VListItem>
                    </VList>
          </div>
                </template>

                <!-- User-to-User: Show Source Users DataTable -->
                <template v-else>
                  <VAlert
                    v-if="!selectedCampaign"
                    type="info"
                    variant="tonal"
            class="mb-4"
          >
                    {{ t('leadAssignment.selectCampaignFirst') }}
                  </VAlert>
                  <div v-else>
                    <!-- Group Status Filter -->
                    <AppSelect
                      v-model="sourceWrapupStatus"
                      :label="t('leadAssignment.filterByStatus')"
                      :placeholder="t('leadAssignment.selectStatusToTransfer')"
                      :items="wrapupStatuses"
                      item-title="name"
                      item-value="id"
                      class="mb-4"
                      clearable
                    >
                      <template #prepend-inner>
                        <VIcon icon="tabler-filter" />
                      </template>
                    </AppSelect>

                    <!-- Source Users DataTable -->
                    <VDataTable
                      v-model="selectedSourceUsers"
                      :headers="sourceUsersHeaders"
                      :items="sourceUsers"
                      item-value="id"
                      show-select
                      :loading="loading"
                      :no-data-text="t('leadAssignment.noUsers')"
                      class="text-no-wrap"
                    >
                      <!-- User Column -->
                      <template #item.user="{ item }">
                        <div class="d-flex align-center gap-2">
                          <VAvatar
                            :image="item.avatar"
                            size="32"
                          />
                          <div>
                            <div class="font-weight-medium">
                              {{ item.firstName }} {{ item.lastName }}
                            </div>
                            <div class="text-caption text-medium-emphasis">
                              {{ item.email }}
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- Lead Count by Status Columns -->
                      <template
                        v-for="status in wrapupStatuses"
                        :key="`source-status-${status.id}`"
                        #[`item.status_${status.id}`]="{ item }"
                      >
                        <VChip
                          size="small"
                          variant="tonal"
                          :color="sourceWrapupStatus === status.id ? 'primary' : getStatusColor(status.wrapupGroupStatus)"
                        >
                          {{ item.leadCounts?.[status.id] || 0 }}
                        </VChip>
                      </template>

                      <!-- Total Column -->
                      <template #item.total="{ item }">
                        <VChip
                          size="small"
                          color="primary"
                          variant="tonal"
                        >
                          {{ calculateTotalLeads(item) }}
                        </VChip>
                      </template>
                    </VDataTable>
          </div>
                </template>
              </VCardText>
            </VCard>
                  </VCol>

          <!-- Right Column: Targets -->
                  <VCol
                    cols="12"
            lg="6"
          >
            <VCard data-tour="target-users">
              <VCardTitle class="d-flex align-center gap-3">
                <VIcon
                  icon="tabler-users"
                  size="24"
                />
                <div>
                  <div class="text-h6">
                    {{ t('leadAssignment.step2Target') }}
                  </div>
                  <div class="text-caption text-medium-emphasis font-weight-regular">
                    {{ t('leadAssignment.step2TargetDesc') }}
                  </div>
                </div>
              </VCardTitle>
              <VCardText>
                <!-- Target Users DataTable -->
                <VDataTable
                  v-model="selectedTargetUsers"
                  :headers="targetUsersHeaders"
                  :items="users"
                  item-value="id"
                  show-select
                  :loading="loading"
                  :no-data-text="t('leadAssignment.noUsers')"
                  class="text-no-wrap"
                >
                  <!-- User Column -->
                  <template #item.user="{ item }">
                    <div class="d-flex align-center gap-2">
                      <VAvatar
                        :image="item.avatar"
                        size="32"
                      />
                      <div>
                        <div class="font-weight-medium">
                          {{ item.firstName }} {{ item.lastName }}
                        </div>
                        <div class="text-caption text-medium-emphasis">
                          {{ item.email }}
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Lead Count by Status Columns -->
                  <template
                    v-for="status in wrapupStatuses"
                    :key="`status-${status.id}`"
                    #[`item.status_${status.id}`]="{ item }"
                  >
                    <VChip
                      size="small"
                      variant="tonal"
                      :color="getStatusColor(status.wrapupGroupStatus)"
                    >
                      {{ item.leadCounts?.[status.id] || 0 }}
                    </VChip>
                  </template>

                  <!-- Total Column -->
                  <template #item.total="{ item }">
                    <VChip
                      size="small"
                      color="primary"
                      variant="tonal"
                    >
                      {{ calculateTotalLeads(item) }}
                    </VChip>
                  </template>
                </VDataTable>
              </VCardText>
            </VCard>
                  </VCol>
                </VRow>
              </VCardText>

      <VDivider />

      <!-- Assign Button -->
      <VCardText>
        <!-- Duplicate Users Warning -->
        <VAlert
          v-if="hasDuplicateUsers"
          type="warning"
          variant="tonal"
          class="mb-4"
        >
          <VAlertTitle>
            <VIcon icon="tabler-alert-triangle" />
            {{ t('leadAssignment.duplicateUsersWarning') }}
          </VAlertTitle>
          <div class="text-body-2">
            {{ t('leadAssignment.duplicateUsersMessage') }}
          </div>
          <div class="mt-2">
            <VChip
              v-for="userId in duplicateUsers"
              :key="userId"
              size="small"
              color="warning"
              class="mr-2"
            >
              {{ users.concat(sourceUsers).find((u: any) => u.id === userId)?.firstName }} 
              {{ users.concat(sourceUsers).find((u: any) => u.id === userId)?.lastName }}
            </VChip>
          </div>
        </VAlert>

        <div class="d-flex justify-end">
              <VBtn
                color="primary"
                size="large"
                :disabled="!canAssign"
                :loading="assigning"
                data-tour="assign-button"
                @click="handleAssign"
              >
                <VIcon
                  icon="tabler-check"
                  start
                />
                {{ t('leadAssignment.assign') }}
              </VBtn>
        </div>
      </VCardText>
    </VCard>
  </div>
</template>
